<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue'
import { Handle, Position, useNode } from '@vue-flow/core'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent
} from '@/components/ui/dropdown-menu'
import { 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Monitor, 
  Play, 
  Pause, 
  Shuffle, 
  Repeat, 
  Repeat1,
  Copy,
  Check,
  X,
  ListMusic,
  ChevronDown,
  ChevronRight
} from 'lucide-vue-next'

// Fixed width for playlist - wider than music nodes (200px) to contain them with padding
const PLAYLIST_FIXED_WIDTH = 240

export interface PlaylistNodeData {
  id: string
  name: string
  shuffle: boolean
  repeat_mode: 'none' | 'all' | 'one'
  volume: number
  playback_target: string | null
  width: number
  height: number
  is_collapsed: boolean
}

interface Props {
  data: PlaylistNodeData
  isPlaying?: boolean
  isMobile?: boolean
  displays: Array<{ id: string; name: string }>
  defaultPlaybackTarget: string
  trackCount?: number
  previewInsertIndex?: number | null  // Index where a dragged item would be inserted
}

const props = defineProps<Props>()

const emit = defineEmits<{
  rename: [id: string, name: string]
  delete: [id: string]
  duplicate: [id: string]
  togglePlayback: [id: string]
  toggleShuffle: [id: string]
  setRepeatMode: [id: string, mode: 'none' | 'all' | 'one']
  updatePlaybackTarget: [id: string, target: string]
  toggleCollapse: [id: string]
}>()

const { node } = useNode()

// Editing state
const isEditing = ref(false)
const editName = ref(props.data.name)

// Colors for playlist - gold border when playing, background stays the same
const playlistColor = computed(() => 'hsl(var(--card))')
const borderColor = computed(() => 
  props.isPlaying ? 'var(--gold-solid)' : 'hsl(var(--border))'
)

const startEditing = () => {
  editName.value = props.data.name
  isEditing.value = true
}

const saveEdit = () => {
  if (editName.value.trim()) {
    emit('rename', props.data.id, editName.value.trim())
  }
  isEditing.value = false
}

const cancelEdit = () => {
  editName.value = props.data.name
  isEditing.value = false
}

// No resize handler needed - fixed width

const repeatIcon = computed(() => {
  switch (props.data.repeat_mode) {
    case 'one': return Repeat1
    case 'all': return Repeat
    default: return Repeat
  }
})

const cycleRepeatMode = () => {
  const modes: Array<'none' | 'all' | 'one'> = ['none', 'all', 'one']
  const currentIndex = modes.indexOf(props.data.repeat_mode)
  const nextMode = modes[(currentIndex + 1) % modes.length]
  emit('setRepeatMode', props.data.id, nextMode)
}

// Collapsed state
const COLLAPSED_HEIGHT = 50

const displayHeight = computed(() => {
  return props.data.is_collapsed ? COLLAPSED_HEIGHT : props.data.height
})

const displayWidth = computed(() => {
  return PLAYLIST_FIXED_WIDTH
})

// Animated SVG dimensions for smooth collapse/expand
const animatedSvgHeight = ref(props.data.height)
let animationFrameId: number | null = null

// Cubic bezier function matching CSS cubic-bezier(0.4, 0, 0.2, 1)
const cubicBezier = (t: number): number => {
  const p1x = 0.4, p1y = 0, p2x = 0.2, p2y = 1
  let low = 0, high = 1
  for (let i = 0; i < 8; i++) {
    const mid = (low + high) / 2
    const x = 3 * (1 - mid) * (1 - mid) * mid * p1x + 3 * (1 - mid) * mid * mid * p2x + mid * mid * mid
    if (x < t) low = mid
    else high = mid
  }
  const finalT = (low + high) / 2
  return 3 * (1 - finalT) * (1 - finalT) * finalT * p1y + 3 * (1 - finalT) * finalT * finalT * p2y + finalT * finalT * finalT
}

const animateSvgHeight = (targetHeight: number, duration = 250) => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
  
  const startHeight = animatedSvgHeight.value
  const startTime = performance.now()
  
  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime
    const progress = Math.min(elapsed / duration, 1)
    
    const eased = cubicBezier(progress)
    
    animatedSvgHeight.value = startHeight + (targetHeight - startHeight) * eased
    
    if (progress < 1) {
      animationFrameId = requestAnimationFrame(animate)
    } else {
      animationFrameId = null
    }
  }
  
  animationFrameId = requestAnimationFrame(animate)
}

// Watch for height changes and animate
watch(displayHeight, (newHeight) => {
  animateSvgHeight(newHeight)
}, { immediate: true })

onUnmounted(() => {
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId)
  }
})

const toggleCollapse = () => {
  emit('toggleCollapse', props.data.id)
}

// SVG unique ID for gradients
const uniqueId = computed(() => `playlist-${props.data.id}-${Math.random().toString(36).slice(2, 9)}`)
</script>

<template>
  <div 
    class="playlist-node"
    :class="{ 'playlist-playing': isPlaying, 'is-collapsed': data.is_collapsed }"
    :style="{
      width: `${displayWidth}px`,
      height: `${displayHeight}px`,
      '--playlist-bg': playlistColor,
      '--playlist-border': borderColor
    }"
  >
    <!-- Source handle (left) - for connecting to scenes -->
    <Handle 
      type="source" 
      :position="Position.Left" 
      id="audio-out"
      class="playlist-handle"
    />
    
    <!-- Decorative SVG Frame -->
    <svg 
      class="playlist-frame-svg"
      :viewBox="`0 0 ${displayWidth} ${animatedSvgHeight}`"
      preserveAspectRatio="none"
    >
      <defs>
        <!-- Gradient for frame -->
        <linearGradient :id="`${uniqueId}-gradient`" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" :stop-color="borderColor" stop-opacity="0.8" />
          <stop offset="50%" :stop-color="borderColor" stop-opacity="1" />
          <stop offset="100%" :stop-color="borderColor" stop-opacity="0.8" />
        </linearGradient>
        
        <!-- Glow filter -->
        <filter :id="`${uniqueId}-glow`" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="2" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        
        <!-- Clip path for chamfered corners -->
        <clipPath :id="`${uniqueId}-clip`">
          <path
            :d="`
              M 16 2
              L ${displayWidth - 16} 2
              L ${displayWidth - 2} 16
              L ${displayWidth - 2} ${animatedSvgHeight - 16}
              L ${displayWidth - 16} ${animatedSvgHeight - 2}
              L 16 ${animatedSvgHeight - 2}
              L 2 ${animatedSvgHeight - 16}
              L 2 16
              Z
            `"
          />
        </clipPath>
      </defs>
      
      <!-- Background with chamfered clip -->
      <rect 
        x="0" 
        y="0" 
        :width="displayWidth" 
        :height="animatedSvgHeight"
        :fill="playlistColor"
        :clip-path="`url(#${uniqueId}-clip)`"
      />
      
      <!-- Main border with chamfered corners -->
      <path
        :d="`
          M 16 2
          L ${displayWidth - 16} 2
          L ${displayWidth - 2} 16
          L ${displayWidth - 2} ${animatedSvgHeight - 16}
          L ${displayWidth - 16} ${animatedSvgHeight - 2}
          L 16 ${animatedSvgHeight - 2}
          L 2 ${animatedSvgHeight - 16}
          L 2 16
          Z
        `"
        fill="none"
        :stroke="`url(#${uniqueId}-gradient)`"
        stroke-width="2"
        stroke-linecap="square"
        :filter="isPlaying ? `url(#${uniqueId}-glow)` : undefined"
      />
      
      <!-- Corner accents - only show when playing -->
      <template v-if="isPlaying">
      <!-- Corner accents - top left -->
      <path
        :d="`M 2 30 L 2 16 L 16 2 L 30 2`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      
      <!-- Corner accents - top right -->
      <path
        :d="`M ${displayWidth - 30} 2 L ${displayWidth - 16} 2 L ${displayWidth - 2} 16 L ${displayWidth - 2} 30`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      
      <!-- Corner accents - bottom left (only when not collapsed) -->
      <path
        v-if="!data.is_collapsed"
        :d="`M 2 ${animatedSvgHeight - 30} L 2 ${animatedSvgHeight - 16} L 16 ${animatedSvgHeight - 2} L 30 ${animatedSvgHeight - 2}`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      
      <!-- Corner accents - bottom right (only when not collapsed) -->
      <path
        v-if="!data.is_collapsed"
        :d="`M ${displayWidth - 30} ${animatedSvgHeight - 2} L ${displayWidth - 16} ${animatedSvgHeight - 2} L ${displayWidth - 2} ${animatedSvgHeight - 16} L ${displayWidth - 2} ${animatedSvgHeight - 30}`"
        fill="none"
        :stroke="borderColor"
        stroke-width="3"
        stroke-linecap="square"
        opacity="0.9"
      />
      </template>
      
      <!-- Header separator line (only when not collapsed) -->
      <line
        v-if="!data.is_collapsed"
        x1="16"
        y1="50"
        :x2="displayWidth - 16"
        y2="50"
        :stroke="borderColor"
        stroke-width="1"
        opacity="0.3"
      />
    </svg>
    
    <!-- Header - Two row layout -->
    <div class="playlist-header">
      <!-- Row 1: Controls -->
      <div class="header-row header-controls">
        <Button 
          variant="ghost" 
          size="icon" 
          class="collapse-btn" 
          @click="toggleCollapse"
          :title="data.is_collapsed ? 'Expand' : 'Collapse'"
        >
          <ChevronRight v-if="data.is_collapsed" class="h-4 w-4" />
          <ChevronDown v-else class="h-4 w-4" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          class="playlist-play-btn"
          @click="emit('togglePlayback', data.id)"
        >
          <component 
            :is="isPlaying ? Pause : Play" 
            class="h-4 w-4" 
          />
        </Button>
        <span class="track-count">{{ trackCount || 0 }}</span>
        <div class="header-spacer"></div>
        <Button 
          variant="ghost" 
          size="icon" 
          class="control-btn"
          :class="{ active: data.shuffle }"
          @click="emit('toggleShuffle', data.id)"
          title="Shuffle"
        >
          <Shuffle class="h-3 w-3" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          class="control-btn"
          :class="{ active: data.repeat_mode !== 'none' }"
          @click="cycleRepeatMode"
          :title="`Repeat: ${data.repeat_mode}`"
        >
          <component :is="repeatIcon" class="h-3 w-3" />
        </Button>
        
        <!-- Menu -->
        <DropdownMenu>
          <DropdownMenuTrigger as-child>
            <Button variant="ghost" size="icon" class="h-5 w-5 playlist-menu-btn">
              <MoreHorizontal class="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" class="w-48">
            <DropdownMenuItem @click="startEditing">
              <Pencil class="h-4 w-4 mr-2" />
              Rename
            </DropdownMenuItem>
            <DropdownMenuItem @click="emit('duplicate', data.id)">
              <Copy class="h-4 w-4 mr-2" />
              Duplicate
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <!-- Playback Target Selection -->
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Monitor class="h-4 w-4 mr-2" />
                Play on
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuRadioGroup 
                  :model-value="data.playback_target || defaultPlaybackTarget"
                  @update:model-value="(v) => emit('updatePlaybackTarget', data.id, v)"
                >
                  <DropdownMenuRadioItem 
                    v-for="display in displays" 
                    :key="display.id" 
                    :value="display.id"
                  >
                    {{ display.name }}
                  </DropdownMenuRadioItem>
                </DropdownMenuRadioGroup>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem class="text-destructive" @click="emit('delete', data.id)">
              <Trash2 class="h-4 w-4 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      <!-- Row 2: Name -->
      <div class="header-row header-name-row" v-if="!isEditing">
        <span class="playlist-name">{{ data.name }}</span>
      </div>
      <div class="header-row playlist-edit" v-else>
        <Input 
          v-model="editName"
          class="h-6 text-xs flex-1"
          @keyup.enter="saveEdit"
          @keyup.escape="cancelEdit"
          autofocus
        />
        <Button variant="ghost" size="icon" class="h-5 w-5" @click="saveEdit">
          <Check class="h-3 w-3" />
        </Button>
        <Button variant="ghost" size="icon" class="h-5 w-5" @click="cancelEdit">
          <X class="h-3 w-3" />
        </Button>
      </div>
    </div>
    
    <!-- Content area for nested music nodes (hidden when collapsed) -->
    <div v-if="!data.is_collapsed" class="playlist-content">
      <div v-if="!trackCount" class="playlist-empty">
        <ListMusic class="h-8 w-8 opacity-30 mb-2" />
        <span>Drag audio nodes here</span>
      </div>
      <!-- Child nodes are rendered by Vue Flow, positioned vertically by SceneGraphView -->
    </div>
  </div>
</template>

<style scoped>
.playlist-node {
  position: relative;
  background: transparent;
  border-radius: 8px;
  width: 240px;
  min-height: 150px;
  overflow: visible;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
}

.playlist-node.is-collapsed {
  min-height: 50px;
}

/* Smooth transition for border color changes */
.playlist-frame-svg path,
.playlist-frame-svg rect,
.playlist-frame-svg line {
  transition: stroke 400ms ease, fill 400ms ease, opacity 400ms ease, filter 400ms ease;
}

.playlist-playing .playlist-frame-svg path[stroke-width="2"] {
  filter: drop-shadow(0 0 6px var(--playlist-border));
}

.playlist-frame-svg {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: visible;
}

.playlist-header {
  position: absolute;
  top: 6px;
  left: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  z-index: 10;
}

.header-row {
  display: flex;
  align-items: center;
  width: 100%;
}

.header-controls {
  gap: 2px;
}

.header-spacer {
  flex: 1;
}

.header-name-row {
  justify-content: center;
  padding: 0 4px;
}

.track-count {
  font-size: 10px;
  color: hsl(var(--muted-foreground));
  margin-left: 4px;
}

.collapse-btn {
  width: 20px;
  height: 20px;
  flex-shrink: 0;
  color: var(--playlist-border);
  opacity: 0.7;
  transition: opacity 0.15s ease;
}

.collapse-btn:hover {
  opacity: 1;
  background: hsl(var(--accent));
}

.playlist-play-btn {
  width: 22px;
  height: 22px;
  flex-shrink: 0;
  color: var(--playlist-border);
}

.playlist-play-btn:hover {
  background: hsl(var(--accent));
}

.playlist-name {
  font-size: 12px;
  font-weight: 600;
  color: hsl(var(--foreground));
  text-transform: uppercase;
  letter-spacing: 0.03em;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.3);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  line-height: 1.2;
  text-align: center;
}

/* When playing, use the golden color for name */
.playlist-playing .playlist-name {
  color: var(--playlist-border);
}

.playlist-name {
  transition: color 400ms ease;
}

.playlist-edit {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.control-btn {
  width: 20px;
  height: 20px;
  opacity: 0.5;
  transition: opacity 150ms, color 150ms;
}

.control-btn:hover {
  opacity: 1;
}

.control-btn.active {
  opacity: 1;
  color: hsl(var(--primary));
}

/* When playing, use gold color for active controls */
.playlist-playing .control-btn.active {
  color: var(--playlist-border);
}

.playlist-menu-btn {
  opacity: 0.5;
  transition: opacity 150ms;
}

.playlist-node:hover .playlist-menu-btn {
  opacity: 1;
}

.playlist-content {
  position: absolute;
  inset: 56px 16px 16px 16px;
  pointer-events: none;
  display: flex;
  flex-direction: column;
}

.playlist-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: hsl(var(--muted-foreground));
  font-size: 12px;
  text-align: center;
  opacity: 0.7;
}

.playlist-handle {
  width: 10px !important;
  height: 10px !important;
  background: var(--playlist-border) !important;
  border: 2px solid hsl(var(--background)) !important;
  transition: background 400ms ease !important;
}

</style>
